import { memo, useState } from 'react'
import { ImageIcon, X } from 'lucide-react'
import { twMerge } from 'tailwind-merge'
import { Button, PreviewCard } from '@highlight/ui'
import { ImageView, useImageSource } from '@renderer/components/image-view'

function RemoveButton({ onClick, label = 'Remove attachment' }: { onClick: () => void; label?: string }) {
  return (
    <Button
      variant="secondary"
      shape="square"
      size="md"
      onClick={(e) => {
        e.stopPropagation()
        onClick()
      }}
      className="absolute right-1.5 top-1.5 z-[1]"
      aria-label={label}
    >
      <X />
    </Button>
  )
}

// ---------------------------------------------------------------------------
// ReminderImagePreview — full-size inline preview
// Accepts either `src` (blob URL) or `fileId` (server-fetched).
// ---------------------------------------------------------------------------

interface ReminderImagePreviewProps {
  src?: string
  fileId?: string
  alt?: string
  onRemove?: () => void
  className?: string
}

export const ReminderImagePreview = memo(function ReminderImagePreview({
  src: srcProp,
  fileId,
  alt = 'Attachment',
  onRemove,
  className,
}: ReminderImagePreviewProps) {
  const { src, loading, error } = useImageSource({ src: srcProp, fileId })
  const [viewOpen, setViewOpen] = useState(false)

  if (error) return null
  if (loading || !src) {
    return (
      <div
        className={twMerge(
          'bg-element corner-smooth flex h-[120px] w-full items-center justify-center overflow-hidden rounded-xl border',
          className,
        )}
      >
        <div className="bg-element-hover size-5 animate-pulse rounded-full" />
      </div>
    )
  }

  return (
    <>
      <div
        className={twMerge(
          'bg-element corner-smooth relative w-full cursor-pointer overflow-hidden rounded-xl border',
          className,
        )}
        onClick={() => setViewOpen(true)}
      >
        {onRemove ? <RemoveButton onClick={onRemove} label={`Remove ${alt}`} /> : null}
        <img src={src} alt={alt} className="mx-auto max-h-[240px] w-full object-contain" loading="lazy" />
      </div>
      <ImageView src={src} alt={alt} open={viewOpen} onOpenChange={setViewOpen} />
    </>
  )
})

// ---------------------------------------------------------------------------
// ReminderThumbnail — compact chip with hover preview
// Accepts either `src` (blob URL) or `fileId` (server-fetched).
// ---------------------------------------------------------------------------

interface ReminderThumbnailProps {
  src?: string
  fileId?: string
  uploading?: boolean
  onRemove?: () => void
}

export const ReminderThumbnail = memo(function ReminderThumbnail({
  src: srcProp,
  fileId,
  uploading,
  onRemove,
}: ReminderThumbnailProps) {
  const { src, loading } = useImageSource({ src: srcProp, fileId })
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [viewOpen, setViewOpen] = useState(false)
  const canRemove = typeof onRemove === 'function'
  const showPlaceholder = uploading || loading || !src

  const stub = (
    <div
      className={twMerge(
        'group/attachment border-border text-foreground-secondary hover:text-foreground hover:bg-element-hover corner-smooth flex h-7 w-fit flex-shrink-0 items-center gap-1.5 rounded-lg border p-1 pr-2 transition-colors duration-200',
        'relative max-w-36 select-none',
        showPlaceholder ? 'cursor-default' : 'cursor-pointer',
      )}
      onClick={() => !showPlaceholder && setViewOpen(true)}
    >
      <div
        className={twMerge(
          'corner-smooth pointer-events-none aspect-square h-5 w-5 flex-shrink-0 overflow-hidden rounded-md',
          canRemove && 'opacity-100 transition-opacity group-hover/attachment:opacity-0',
        )}
      >
        {showPlaceholder ? (
          <div className="bg-element-hover flex h-full w-full items-center justify-center">
            <ImageIcon className={twMerge('text-foreground-placeholder size-3', uploading && 'animate-pulse')} />
          </div>
        ) : (
          <img
            src={src}
            className={twMerge('h-full w-full object-cover transition-opacity', !isImageLoaded && 'opacity-0')}
            onLoad={() => setIsImageLoaded(true)}
          />
        )}
      </div>
      <span className="type-ui-sm truncate">{uploading ? 'Uploading' : 'Image'}</span>
      {canRemove && (
        <div
          className={twMerge(
            'absolute bottom-0 left-0 top-0 flex aspect-square w-fit items-center justify-center',
            'pointer-events-none opacity-0 transition-opacity',
            'group-hover/attachment:pointer-events-auto group-hover/attachment:opacity-100',
          )}
        >
          <div
            className="text-foreground-secondary hover:text-foreground h-fit w-fit cursor-pointer transition-colors"
            onClick={(e) => {
              e.stopPropagation()
              onRemove()
            }}
          >
            <X className="size-3.5" />
          </div>
        </div>
      )}
    </div>
  )

  if (showPlaceholder) return stub

  return (
    <>
      <PreviewCard>
        <PreviewCard.Trigger className="inline-flex h-auto text-inherit no-underline hover:no-underline">
          {stub}
        </PreviewCard.Trigger>
        <PreviewCard.Content
          className="border-border max-w-72 gap-0 overflow-hidden bg-transparent p-0"
          sideOffset={8}
          showBackdrop={false}
          positionerProps={{ side: 'top' }}
        >
          <img src={src} alt="Attachment preview" className="block w-full rounded-[inherit] object-contain" />
        </PreviewCard.Content>
      </PreviewCard>
      <ImageView src={src!} alt="Attachment" open={viewOpen} onOpenChange={setViewOpen} />
    </>
  )
})
